import React, { useState, useMemo, useEffect } from "react";
import Papa from "papaparse"; // Import the CSV parser
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  LayoutDashboard,
  Building2,
  Target,
  ExternalLink,
  MessageSquare,
  Activity,
  Zap,
  Loader2,
  ListFilter,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// --- Utils ---
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
];

// --- Components ---
const Card = ({ children, className }) => (
  <div
    className={cn(
      "bg-slate-900 border border-slate-800 rounded-xl overflow-hidden",
      className
    )}
  >
    {children}
  </div>
);

const Badge = ({ children, variant = "default" }) => {
  const styles = {
    default: "bg-slate-800 text-slate-400 border-slate-700",
    intent: "bg-emerald-950 text-emerald-400 border-emerald-900",
    brand: "bg-blue-950 text-blue-400 border-blue-900",
    pillar: "bg-purple-950 text-purple-400 border-purple-900",
    risk: "bg-red-950 text-red-400 border-red-900",
  };
  return (
    <span
      className={cn(
        "px-2 py-0.5 rounded text-[10px] font-medium border uppercase tracking-wide",
        styles[variant]
      )}
    >
      {children}
    </span>
  );
};

export default function ABMDashboard() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState("ALL");

  // --- FETCH & PARSE CSV ON MOUNT ---
  useEffect(() => {
    // 1. Fetch the CSV file from the public folder
    fetch("/coursera_for_business_linkedin_account_chatter.csv")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load CSV file");
        }
        return response.text(); // Get text, not JSON
      })
      .then((csvText) => {
        // 2. Parse CSV text into JSON objects
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            // Clean up the data exactly like we did before
            const processed = results.data
              .filter((row) => row.CompanyHit && row.CompanyHit.trim() !== "")
              .map((row, idx) => ({
                ...row,
                id: idx,
                // Ensure these fields exist to prevent crashes
                Pillars: row.Pillars || "",
                Themes: row.Themes || "",
                Snippet: row.Snippet || "",
                Title: row.Title || "",
                CompanyHit: row.CompanyHit || "Unknown",
                PostDate: row.PostDate || "Unknown Date",
              }));
            
            setData(processed);
            setIsLoading(false);
          },
        });
      })
      .catch((error) => {
        console.error("Error loading ABM data:", error);
        setIsLoading(false);
      });
  }, []);

  // --- Aggregations ---

  // 1. Unique Accounts & Signal Volume
  const accountStats = useMemo(() => {
    const counts = {};
    data.forEach((d) => {
      const acc = d.CompanyHit;
      if (acc) counts[acc] = (counts[acc] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1]) // Sort by volume
      .map(([name, count]) => ({ name, count }));
  }, [data]);

  // 2. Filtered Data based on Selection
  const filteredData = useMemo(() => {
    if (selectedAccount === "ALL") return data;
    return data.filter((d) => d.CompanyHit === selectedAccount);
  }, [data, selectedAccount]);

  // 3. Pillar/Topic Stats
  const pillarStats = useMemo(() => {
    const counts = {};
    filteredData.forEach((d) => {
      if (d.Pillars) {
        d.Pillars.split("|").forEach((p) => {
          const cleanP = p.trim();
          if (cleanP) counts[cleanP] = (counts[cleanP] || 0) + 1;
        });
      }
    });
    return Object.entries(counts)
      .map(([subject, A]) => ({ subject, A }))
      .sort((a, b) => b.A - a.A)
      .slice(0, 6);
  }, [filteredData]);

  // 4. Intent/Theme Stats
  const themeStats = useMemo(() => {
    const counts = {};
    filteredData.forEach((d) => {
      if (d.Themes) {
        d.Themes.split("|").forEach((t) => {
          const cleanT = t.trim();
          if (cleanT) counts[cleanT] = (counts[cleanT] || 0) + 1;
        });
      }
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [filteredData]);

  // Calculate High Intent Signals count
  const highIntentCount = useMemo(() => {
    return filteredData.filter((d) =>
      (d.Snippet + d.Title)
        .toLowerCase()
        .match(/rfp|switch|alternative|vs|pricing|cost/)
    ).length;
  }, [filteredData]);

  // --- LOADING STATE ---
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-400">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
        <p className="text-sm font-medium">Loading ABM Intelligence...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans flex overflow-hidden">
      {/* --- LEFT SIDEBAR: ACCOUNT LIST (The ABM Core) --- */}
      <aside className="w-72 border-r border-slate-800 bg-slate-900/50 flex flex-col h-screen shrink-0">
        <div className="p-4 border-b border-slate-800 bg-slate-900">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-blue-500" />
            <h1 className="font-bold text-white tracking-tight">ABM Radar</h1>
          </div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">
            {accountStats.length} Active Accounts
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
          <button
            onClick={() => setSelectedAccount("ALL")}
            className={cn(
              "w-full flex items-center justify-between p-3 rounded-lg text-sm transition-all",
              selectedAccount === "ALL"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-900/20"
                : "text-slate-400 hover:bg-slate-800 hover:text-white"
            )}
          >
            <span className="font-medium flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4" /> Global View
            </span>
            <span className="bg-slate-950/30 px-2 py-0.5 rounded text-xs">
              {data.length}
            </span>
          </button>

          <div className="h-px bg-slate-800 my-2 mx-2" />

          <div className="px-2 py-1 text-[10px] font-bold text-slate-500 uppercase">
            Accounts by Signal Vol
          </div>

          {accountStats.map((acc) => (
            <button
              key={acc.name}
              onClick={() => setSelectedAccount(acc.name)}
              className={cn(
                "w-full flex items-center justify-between p-3 rounded-lg text-sm transition-all group border",
                selectedAccount === acc.name
                  ? "bg-slate-800 text-white border-slate-700"
                  : "text-slate-400 border-transparent hover:bg-slate-800/50 hover:text-white"
              )}
            >
              <span className="font-medium flex items-center gap-2 truncate">
                <Building2
                  className={cn(
                    "w-4 h-4",
                    selectedAccount === acc.name
                      ? "text-blue-400"
                      : "text-slate-600"
                  )}
                />
                {acc.name}
              </span>
              <span
                className={cn(
                  "px-2 py-0.5 rounded text-[10px] font-mono",
                  selectedAccount === acc.name
                    ? "bg-slate-950 text-blue-400"
                    : "bg-slate-900 text-slate-600 group-hover:text-slate-400"
                )}
              >
                {acc.count}
              </span>
            </button>
          ))}
        </div>
      </aside>

      {/* --- MAIN CONTENT AREA --- */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-slate-800 bg-slate-900/50 backdrop-blur flex items-center justify-between px-6 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              {selectedAccount === "ALL"
                ? "Global Market Intelligence"
                : selectedAccount}
              {selectedAccount !== "ALL" && (
                <Badge variant="brand">Focus Account</Badge>
              )}
            </h2>
            <p className="text-xs text-slate-500">
              {selectedAccount === "ALL"
                ? `Monitoring chatter across all target accounts`
                : `Analyzing intent signals and market chatter for ${selectedAccount}`}
            </p>
          </div>
          <div className="flex gap-6 text-xs text-slate-400">
            <div className="flex flex-col items-end">
              <span className="text-slate-500 uppercase text-[10px] font-bold">
                Total Signals
              </span>
              <div className="flex items-center gap-2 text-white font-mono font-bold text-lg">
                <Activity className="w-4 h-4 text-slate-500" />
                {filteredData.length}
              </div>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-slate-500 uppercase text-[10px] font-bold">
                High Intent
              </span>
              <div className="flex items-center gap-2 text-white font-mono font-bold text-lg">
                <Zap className="w-4 h-4 text-emerald-500" />
                {highIntentCount}
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Dashboard Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* TOP ROW: VISUALIZATION */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-80">
            {/* Chart 1: Topic Landscape */}
            <Card className="col-span-2 p-5 flex flex-col relative">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 absolute top-5 left-5">
                {selectedAccount === "ALL"
                  ? "Share of Voice by Topic"
                  : `What ${selectedAccount} is discussing`}
              </h3>
              <div className="flex-1 w-full min-h-0 mt-6">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={pillarStats}
                    layout="vertical"
                    margin={{ left: 0, right: 30, top: 10, bottom: 5 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="subject"
                      type="category"
                      width={180}
                      tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0f172a",
                        borderColor: "#1e293b",
                        color: "#fff",
                        borderRadius: "8px",
                      }}
                      cursor={{ fill: "#334155", opacity: 0.1 }}
                    />
                    <Bar
                      dataKey="A"
                      fill="#3b82f6"
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                    >
                      {pillarStats.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Chart 2: Intent breakdown */}
            <Card className="p-5 flex flex-col">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                Top Detected Themes
              </h3>
              <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar">
                {themeStats.map(([theme, count], i) => (
                  <div key={theme} className="group cursor-default">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 font-medium">
                        {theme}
                      </span>
                      <span className="text-slate-500 font-mono text-xs">
                        {count}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500/80 rounded-full group-hover:bg-emerald-400 transition-colors"
                        style={{
                          width: `${(count / filteredData.length) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
                {themeStats.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-slate-600 text-xs">
                    <ListFilter className="w-8 h-8 mb-2 opacity-20" />
                    No thematic tags found.
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* BOTTOM ROW: FEED */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Signal Feed
              </h3>
              <span className="text-xs text-slate-600">
                Showing {filteredData.length} results
              </span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {filteredData.map((item) => (
                <div
                  key={item.id}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-5 hover:border-blue-500/30 hover:bg-slate-900 transition-all group relative"
                >
                  {/* Date & Company Badge */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                          {item.PostDate || "Unknown Date"}
                        </span>
                        <span className="text-sm font-bold text-blue-400">
                          {item.CompanyHit}
                        </span>
                      </div>
                    </div>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-slate-600 hover:text-white transition-colors p-1 hover:bg-slate-800 rounded"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>

                  {/* Content */}
                  <h4 className="text-slate-200 font-medium mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                    {item.Title}
                  </h4>
                  <p className="text-sm text-slate-400 line-clamp-3 mb-4 leading-relaxed">
                    "{item.Snippet}"
                  </p>

                  {/* Tags Footer */}
                  <div className="flex flex-wrap gap-2 mt-auto pt-4 border-t border-slate-800/50">
                    {item.Pillars &&
                      item.Pillars.split("|")
                        .slice(0, 2)
                        .map((p) => (
                          <Badge key={p} variant="pillar">
                            {p.trim()}
                          </Badge>
                        ))}
                    {item.Themes &&
                      item.Themes.split("|")
                        .slice(0, 2)
                        .map((t) => (
                          <Badge key={t} variant="intent">
                            {t.trim()}
                          </Badge>
                        ))}
                    {/* Platform Tag */}
                    <span className="ml-auto text-[10px] text-slate-600 px-2 py-0.5 font-mono">
                      {item.Platform}
                    </span>
                  </div>
                </div>
              ))}

              {filteredData.length === 0 && (
                <div className="col-span-full py-16 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg bg-slate-900/20">
                  <Activity className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p>No signals found for this selection.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
